import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useAuth } from '../App';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { user } = useAuth();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Human First', path: '/human-first' },
    { name: 'A³ Assessment', path: '/a3-assessment' },
    { name: 'Blog', path: '/blog' },
    { name: 'Book', path: '/booking' },
    { name: 'Contact', path: '/contact' },
  ];

  // Determine where the user button should link based on role
  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'coach') return '/coach';
    if (user.role === 'admin' || user.role === 'superadmin') return '/admin';
    return '/dashboard';
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E7E5E4]">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_golf-experience-hub/artifacts/amevwgso_logo-for-web%20copy.jpg" 
              alt="Quiet Mind Golf" 
              className="h-12 w-auto"
            />
            <span className="font-['Playfair_Display'] text-xl font-medium text-[#1A1A1A] hidden sm:block">
              Quiet Mind Golf
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-[#57534E] hover:text-[#2C5530] text-sm font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to={getDashboardPath()}
              className="text-[#2C5530] text-sm font-medium hover:text-[#1E3A21] transition-colors"
              data-testid="header-login-btn"
            >
              {user ? 'My Dashboard' : 'Client Login'}
            </Link>
            {!user && (
              <Link
                to="/coach-login"
                className="text-[#57534E] text-sm font-medium hover:text-[#2C5530] transition-colors"
                data-testid="header-coach-login-btn"
              >
                Coach Login
              </Link>
            )}
            <Link
              to="/contact"
              className="bg-[#2C5530] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 flex items-center gap-2"
              data-testid="header-cta-btn"
            >
              Get Started
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#1A1A1A]"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-6 border-t border-[#E7E5E4]" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#57534E] hover:text-[#2C5530] text-base font-medium py-2"
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-[#E7E5E4] mt-2 flex flex-col gap-3">
                <Link
                  to={getDashboardPath()}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-[#2C5530] text-base font-medium py-2"
                >
                  {user ? 'My Dashboard' : 'Client Login'}
                </Link>
                {!user && (
                  <Link
                    to="/coach-login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-[#57534E] text-base font-medium py-2"
                  >
                    Coach Login
                  </Link>
                )}
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="bg-[#2C5530] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider text-center"
                >
                  Get Started
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
